import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, TextField, Button, Alert, FormControlLabel, Checkbox, Avatar } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import { useAuth } from '../../../contexts/AuthContext';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';
const CDN_URL = 'https://cdn-aims.amy.care';

interface SiteSettings {
  site_title: string;
  site_description?: string;
  logo_url?: string;
  primary_color?: string;
}

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Default to checked
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [logoError, setLogoError] = useState(false); // Track logo load errors
  const { login } = useAuth();
  const navigate = useNavigate();

  // Helper function to get full logo URL
  const getLogoUrl = (logoPath?: string): string | undefined => {
    if (!logoPath) return undefined;
    
    // If it's already an absolute URL, return as is
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    // If it starts with '/cdn/', remove '/cdn' and prepend CDN_URL
    if (logoPath.startsWith('/cdn/')) {
      return `${CDN_URL}${logoPath.substring(4)}`; // substring(4) gives us '/documents/...'
    }
    // If it's a relative path starting with 'cdn/', convert to full CDN URL
    if (logoPath.startsWith('cdn/')) {
      return `${CDN_URL}/${logoPath.substring(4)}`; // Remove 'cdn/' prefix
    }
    // Otherwise, assume it's a CDN path
    return `${CDN_URL}/${logoPath}`;
  };

  useEffect(() => {
    // Fetch site settings on component mount
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/settings/site`);
        setSiteSettings(response.data);
      } catch (err) {
        console.error('Failed to fetch site settings:', err);
        // Use default values if fetch fails
        setSiteSettings({
          site_title: 'AIMS SRU',
          site_description: 'Alumni Management System',
        });
      }
    };
    fetchSiteSettings();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    console.log('Login form submitted', { email, password: '***' });
    setError('');
    setLoading(true);

    try {
      console.log('Calling login...');
      await login(email, password, rememberMe); // Pass rememberMe flag
      console.log('Login successful, navigating to dashboard');
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcher />
      </Box>
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          {(() => {
            const logoUrl = getLogoUrl(siteSettings?.logo_url);
            
            if (logoUrl && !logoError) {
              return (
                <Avatar
                  key={logoUrl}
                  src={logoUrl}
                  alt="Site Logo"
                  imgProps={{
                    style: { objectFit: 'contain' }
                  }}
                  onError={() => setLogoError(true)}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 2,
                    bgcolor: 'transparent',
                  }}
                />
              );
            } else {
              return (
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: siteSettings?.primary_color || 'primary.main',
                    mb: 2,
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 48 }} />
                </Avatar>
              );
            }
          })()}
          <Typography 
            variant="h4" 
            gutterBottom 
            align="center" 
            sx={{ 
              fontWeight: 600, 
              color: siteSettings?.primary_color || 'primary.main', 
              mb: 0.5 
            }}
          >
            {siteSettings?.site_title || 'AIMS SRU'}
          </Typography>
          <Typography variant="h6" gutterBottom align="center" sx={{ mb: 1 }}>
            {siteSettings?.site_description || 'Alumni Management System'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Admin Panel Login
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Admin Email"
            type="email"
            margin="normal"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            placeholder="admin@sru.ac.th"
            helperText="Only admin accounts can access this panel"
            autoComplete="email"
          />
          
          <TextField
            fullWidth
            label={t('auth.password')}
            type="password"
            margin="normal"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                color="primary"
              />
            }
            label="Keep me logged in (365 days)"
            sx={{ mt: 1 }}
          />
          
          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? `${t('common.loading')}` : t('auth.login')}
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          Default: admin@sru.ac.th / admin123
        </Typography>
      </Paper>
    </Box>
  );
}

export default Login;
